/**
 * MerchSection — the creator's "Shop & Merch" block (MERCH-ATTR slice 3).
 *
 * Public view: product tiles + store-link fallbacks.
 *
 * Owner view (when the owner-edit callbacks are supplied — mirrors the
 * onSave-presence gate used across the dashboard sections): connect/disconnect
 * a shop URL, start-a-shop quick-links when there's none, and per-item controls
 * to correct the price or delete a wrong item. The server
 * enforces real ownership on every mutation, so these are optimistic UI only.
 *
 * On the desk: one sheet holds the store. The connect form is a manila inset
 * with one ruled line to write the link on; connected shops are punched tags;
 * each product is a punched price tag (name and price). Its heading
 * carries the tee sticker, like every section of the dashboard and profile.
 */
import React, { useMemo, useState } from 'react';
import { Card, SectionHeader } from '../dashboard/sections';
import type { MerchProduct } from '../../services/dataService';
import NavMark from '../brand/NavMark';

const _CURRENCY_SYMBOL: Record<string, string> = { USD: '$', CAD: '$', AUD: '$', GBP: '£', EUR: '€' };
function priceLabel(min: number | null, max: number | null, currency: string | null): string | null {
  if (min == null) return null;
  const sym = currency ? (_CURRENCY_SYMBOL[currency] || '') : '';
  const suffix = currency && !_CURRENCY_SYMBOL[currency] ? ` ${currency}` : '';
  const f = (n: number) => `${sym}${Number.isInteger(n) ? n : n.toFixed(2)}${suffix}`;
  if (max != null && max > min) return `${f(min)}–${f(max)}`;
  return f(min);
}

/** Quick-start links for creators who don't have a shop yet. External nav. */
const START_SHOP_PLATFORMS: { name: string; url: string; note: string }[] = [
  { name: 'Shopify', url: 'https://www.shopify.com/free-trial', note: 'Full store — we auto-import products' },
  { name: 'Fourthwall', url: 'https://fourthwall.com/', note: 'Creator merch, no upfront cost' },
  { name: 'Big Cartel', url: 'https://www.bigcartel.com/', note: 'Simple, free for small catalogs' },
];

export interface MerchOwnerHandlers {
  busy: boolean;
  onAddShop: (url: string) => Promise<void>;
  onRemoveShop: (domain: string) => Promise<void>;
  onEditItem: (itemId: string, fields: { price_min?: number }) => Promise<void>;
  onDeleteItem: (itemId: string) => Promise<void>;
}

interface Props {
  firstName: string;
  merchProducts: MerchProduct[];
  merchFallbackShops: { domain: string; url: string }[];
  /** Present only for the profile owner — unlocks the edit controls. */
  owner?: MerchOwnerHandlers;
}

const MerchSection: React.FC<Props> = ({ firstName, merchProducts, merchFallbackShops, owner }) => {
  const isOwner = !!owner;
  const hasAnything = merchProducts.length > 0 || merchFallbackShops.length > 0;

  const connectedShops = useMemo(() => {
    const s = new Set<string>();
    merchProducts.forEach(p => p.shop_domain && s.add(p.shop_domain));
    merchFallbackShops.forEach(f => f.domain && s.add(f.domain));
    return Array.from(s);
  }, [merchProducts, merchFallbackShops]);

  // A visitor whose creator has no shop on file sees the section anyway, saying so: a missing
  // section read as "we never looked", when the truth is "nothing connected yet".
  if (!hasAnything && !isOwner) {
    return (
      <section>
        <SectionHeader doodle="tee" tone="rust" title="Shop & Merch" subtitle={`Products and merch ${firstName} sells directly.`} />
        <Card>
          <p className="br-ruled-empty">
            {firstName} hasn’t connected a shop yet. It appears here once they connect one, or once Bearri finds
            their own store linked in their posts.
          </p>
        </Card>
      </section>
    );
  }

  return (
    <section>
      <SectionHeader doodle="tee" tone="rust" title="Shop & Merch" subtitle={`Products and merch ${firstName} sells directly.`} />
      <Card>
        <div className="mb-3.5">
          <h3 className="br-h3 br-h3--marked"><NavMark name="shop" />{isOwner ? 'Your Store' : 'Their Store'}</h3>
          <p className="br-small mt-px">
            {merchProducts.length > 0
              ? `${merchProducts.length} item${merchProducts.length === 1 ? '' : 's'} — ${isOwner ? 'your' : 'their'} store, kept separate from sponsor deals.`
              : `${isOwner ? 'Your' : 'Their'} store — kept separate from sponsor deals.`}
          </p>
        </div>

        {isOwner && owner && (
          <OwnerShopBar
            owner={owner}
            connectedShops={connectedShops}
            hasProducts={merchProducts.length > 0}
          />
        )}

        {merchProducts.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {merchProducts.map(prod => (
              <MerchTile key={prod.id} prod={prod} owner={owner} />
            ))}
          </div>
        )}

        {merchFallbackShops.length > 0 && (
          <>
            {merchProducts.length > 0 && <hr className="br-rule my-3.5" />}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {merchFallbackShops.map(shop => (
                <a key={shop.domain} href={shop.url} target="_blank" rel="noreferrer noopener"
                  className="br-sheet br-sheet--flat br-sheet--hover flex items-center gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <div className="br-h3 truncate">{shop.domain}</div>
                    <span className="br-inkline text-[11px]">Visit shop →</span>
                  </div>
                </a>
              ))}
            </div>
          </>
        )}

        {isOwner && !hasAnything && (
          <p className="br-lede mt-1">
            No shop connected yet. Paste your shop link above, or start one below.
          </p>
        )}
      </Card>
    </section>
  );
};

/** Owner-only bar: connect a shop, list/disconnect connected shops, and
 *  start-a-shop quick-links when there's nothing yet. */
const OwnerShopBar: React.FC<{
  owner: MerchOwnerHandlers;
  connectedShops: string[];
  hasProducts: boolean;
}> = ({ owner, connectedShops, hasProducts }) => {
  const [url, setUrl] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const connect = async () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    setNote(null);
    try {
      await owner.onAddShop(trimmed);
      setUrl('');
    } catch (e) {
      setNote((e as Error)?.message || 'Could not connect that shop.');
    }
  };

  return (
    <div className="br-sheet br-sheet--cream br-sheet--plain mb-3.5 p-3.5">
      <label className="br-small block font-semibold" htmlFor="br-shop-link">
        Your shop link{connectedShops.length > 0 ? ' (add another)' : ''} · we import products from Shopify stores
      </label>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <input
          id="br-shop-link"
          type="text"
          className="br-line-input br-line-input--sm w-full min-w-0 flex-1"
          placeholder="yourstore.com"
          value={url}
          onChange={e => setUrl(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') connect(); }}
          disabled={owner.busy}
        />
        <button type="button" onClick={connect} disabled={owner.busy || !url.trim()} className="br-slab br-slab--cream br-slab--sm flex-shrink-0">
          {owner.busy ? 'Connecting…' : 'Connect'}
        </button>
      </div>
      {note && <p className="br-small br-ink-red mt-1.5">{note}</p>}

      {connectedShops.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-2">
          {connectedShops.map(dom => (
            <span key={dom} className="br-tag br-tag--white">
              {dom}
              {/* 40px hit box; negative margins keep the tag at its 30px height */}
              <button
                type="button"
                aria-label={`Disconnect ${dom}`}
                className="br-remove -my-2 -ml-2.5 -mr-3 inline-flex h-10 w-10 items-center justify-center text-sm leading-none"
                disabled={owner.busy}
                onClick={() => { if (window.confirm(`Disconnect ${dom} and remove its products?`)) owner.onRemoveShop(dom); }}
              >×</button>
            </span>
          ))}
        </div>
      )}

      {!hasProducts && (
        <>
          <hr className="br-rule mt-3 mb-2.5" />
          <p className="br-small">
            <span className="font-semibold">Don't have a shop yet? Start one: </span>
            {START_SHOP_PLATFORMS.map((p, i) => (
              <React.Fragment key={p.name}>
                {i > 0 && <span className="br-ink-4"> · </span>}
                <a href={p.url} target="_blank" rel="noreferrer noopener" title={p.note} className="br-ink-blue whitespace-nowrap font-semibold hover:underline">{p.name} →</a>
              </React.Fragment>
            ))}
          </p>
        </>
      )}
    </div>
  );
};

/** A single product: a punched price tag — the photo printed on it, the name,
 *  the price as a figure. Owners get a pen-edit line that
 *  shows on hover (and on focus, for the keyboard). */
const MerchTile: React.FC<{ prod: MerchProduct; owner?: MerchOwnerHandlers }> = ({ prod, owner }) => {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState<string>(prod.price_min == null ? '' : String(prod.price_min));
  const price_lbl = priceLabel(prod.price_min, prod.price_max, prod.currency);

  const savePrice = async () => {
    if (!owner) return;
    const trimmed = price.trim();
    if (trimmed === '' || Number.isNaN(Number(trimmed))) return;
    await owner.onEditItem(prod.id, { price_min: Math.max(0, Number(trimmed)) });
  };

  return (
    <div className="br-price-tag">
      {/* The photo is printed on the tag only when Shopify gave us one; otherwise the tag is ink. */}
      {prod.image_url && (
        <a href={prod.product_url} target="_blank" rel="noreferrer noopener" className="br-price-tag__photo">
          <img src={prod.image_url} alt={prod.title} loading="lazy"
            className="h-full w-full object-cover"
            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
        </a>
      )}
      <a href={prod.product_url} target="_blank" rel="noreferrer noopener" className="br-price-tag__name hover:underline" title={prod.title}>{prod.title}</a>
      <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
        {price_lbl && <span className="br-num text-[13px]">{price_lbl}</span>}
        {!prod.available && <span className="br-count">Sold out</span>}
      </div>

      {owner && (
        !editing ? (
          <button type="button" className="br-price-tag__edit br-ink-blue mt-1.5 self-start text-[11px] font-semibold hover:underline"
            onClick={() => setEditing(true)}>Edit</button>
        ) : (
          <div className="mt-2 space-y-2">
            <div>
              <label className="br-caption block font-semibold">Price</label>
              <div className="flex items-end gap-2">
                <input type="text" className="br-line-input br-line-input--sm w-full min-w-0" value={price} inputMode="decimal"
                  onChange={e => setPrice(e.target.value)} disabled={owner.busy} />
                <button type="button" className="br-slab br-slab--white br-slab--sm" onClick={savePrice} disabled={owner.busy}>Save</button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <button type="button" className="br-caption hover:underline"
                onClick={() => setEditing(false)}>Close</button>
              <button type="button" className="br-remove br-caption font-semibold hover:underline"
                disabled={owner.busy}
                onClick={() => { if (window.confirm(`Delete "${prod.title}"?`)) owner.onDeleteItem(prod.id); }}>
                Delete item
              </button>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default MerchSection;
